import { Link } from 'react-router-dom';
import { LetterCard } from '../components/LetterCard';
import { EmptyState, ErrorState, LoadingState } from '../components/StatePanel';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useAsyncData } from '../hooks/useAsyncData';
import type { Letter } from '../models/Letter';
import { letterService } from '../services/letterService';

/**
 * The published letters, newest first.
 *
 * Reading is open to everybody and writing needs the family invitation, exactly
 * as memories work -- so this invites a letter in the same words the memories
 * page uses. Somebody without the invitation who follows the link is told why
 * by RequireRole rather than being refused without explanation.
 */
export function LettersPage() {
  const { siteConfig } = useSiteConfig();
  const { data, status, error, reload } = useAsyncData<Letter[]>(
    () => letterService.getLetters(),
    'The letters could not be loaded.',
  );

  const name = siteConfig?.name ?? '';

  return (
    <div className="container container--reading">
      <header className="page-header">
        <h1>Letters to Dad</h1>
        <p>
          {name
            ? `Letters written to ${name} by family and friends, with the most recent first. `
            : 'Letters written by family and friends, with the most recent first. '}
          <Link to="/add-letter">Write a letter of your own</Link>.
        </p>
      </header>

      {status === 'loading' ? <LoadingState label="Loading the letters…" /> : null}

      {status === 'error' ? (
        <ErrorState message={error ?? 'The letters could not be loaded.'} onRetry={reload} />
      ) : null}

      {status === 'success' && data !== null ? (
        data.length === 0 ? (
          <EmptyState title="No letters have been shared yet">
            <p>
              When someone writes a letter it will appear here.{' '}
              <Link to="/add-letter">Be the first to write one</Link>.
            </p>
          </EmptyState>
        ) : (
          <div className="stack">
            {data.map((letter) => (
              <LetterCard key={letter.id} letter={letter} />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
